/**
 * features/home/api.ts — queries reutilizables para la Home y "Sobre mí".
 *
 * Cada hook envuelve `useQuery` de TanStack Query contra el singleton
 * `supabase` tipado. Las `queryKey` son únicas por dataset para permitir
 * invalidaciones quirúrgicas desde el panel admin (F6).
 *
 * Reglas de los SELECTs:
 *  - Solo columnas necesarias para las cards (ahorra bytes y respeta RLS).
 *  - Filtros mínimos: `is_active`/`is_published` + `deleted_at is null`.
 *  - Orden y `limit` definidos por la spec §4.2 (Home).
 *  - No se anidan relaciones todavía; F6 podrá expandir con joins.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase/client';
import type { Database } from '../../lib/supabase/types';

// ---------------------------------------------------------------------------
// Tipos derivados del schema. Reflejamos solo las columnas que el SELECT pide.
// ---------------------------------------------------------------------------

type SiteSettingsRow = Database['public']['Tables']['site_settings']['Row'];

/** Site settings completo — la fila singleton. */
export type SiteSettings = SiteSettingsRow;

/** Forma del bloque `hero` en `site_settings.hero` (JSON libre). */
export interface SiteHero {
  title?: string;
  subtitle?: string;
  image_url?: string | null;
  cta_primary?: { label: string; href: string };
  cta_secondary?: { label: string; href: string };
}

/** Forma del bloque `about` en `site_settings.about`. */
export interface SiteAbout {
  story_md?: string;
  image_url?: string | null;
  values?: Array<{ icon: string; label: string }>;
}

/** Forma del bloque `social_links`. Puede ser objeto o array según el seed. */
export interface SiteSocialLinks {
  instagram?: string;
  facebook?: string;
  youtube?: string;
  tiktok?: string;
  [platform: string]: string | undefined;
}

/** Subconjunto de columnas de `therapies` requerido por la card. */
export interface FeaturedTherapy {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  modality: Database['public']['Enums']['therapy_modality'];
  duration_min: number;
  focus_tags: string[];
  cover_url: string | null;
  category_id: string | null;
}

/** Subconjunto de `products` para `ProductCard`. */
export interface FeaturedProduct {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  price_cop: number;
  cover_url: string | null;
  edition: Database['public']['Enums']['product_edition'];
  is_handmade: boolean;
  focus_tags: string[];
}

/** Subconjunto de `events` para card de próximos eventos. */
export interface UpcomingEvent {
  id: string;
  title: string;
  slug: string;
  kind: Database['public']['Enums']['event_kind'];
  modality: Database['public']['Enums']['event_modality'];
  starts_at: string;
  ends_at: string;
  location_name: string | null;
  summary: string | null;
  hero_url: string | null;
  price_cop: number;
  /** Categoría (para reconocer exposiciones, CEC-261009-8e6d). */
  event_categories: { slug: string } | null;
}

/** Subconjunto de `testimonials` mostrado en Home y Sobre mí. */
export interface HomeTestimonial {
  id: string;
  author_name: string;
  avatar_url: string | null;
  body: string;
  rating: number | null;
}

/** Subconjunto de `gallery_items` destacados en Instagram. */
export interface InstagramGalleryItem {
  id: string;
  title: string;
  slug: string;
  type: Database['public']['Enums']['gallery_type'];
  media_urls: string[];
  cover_url: string | null;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Trae la fila singleton de `site_settings`. Cachea con `staleTime` global
 * (30s en `providers.tsx`). Useful para hero/about/social/feature_flags.
 */
export function useSiteSettings(): UseQueryResult<SiteSettings, Error> {
  return useQuery<SiteSettings, Error>({
    queryKey: ['site_settings'],
    queryFn: async (): Promise<SiteSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .eq('singleton', true)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) throw new Error('No se encontró configuración del sitio.');
      return data as SiteSettings;
    },
  });
}

/** 4 terapias activas más recientes para la Home. */
export function useFeaturedTherapies(): UseQueryResult<FeaturedTherapy[], Error> {
  return useQuery<FeaturedTherapy[], Error>({
    queryKey: ['therapies', 'featured'],
    queryFn: async (): Promise<FeaturedTherapy[]> => {
      const { data, error } = await supabase
        .from('therapies')
        .select(
          'id, title, slug, summary, modality, duration_min, focus_tags, cover_url, category_id',
        )
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(4);
      if (error) throw new Error(error.message);
      return (data ?? []) as FeaturedTherapy[];
    },
  });
}

/** 4 productos activos más recientes. */
export function useFeaturedProducts(): UseQueryResult<FeaturedProduct[], Error> {
  return useQuery<FeaturedProduct[], Error>({
    queryKey: ['products', 'featured'],
    queryFn: async (): Promise<FeaturedProduct[]> => {
      const { data, error } = await supabase
        .from('products')
        .select('id, title, slug, summary, price_cop, cover_url, edition, is_handmade, focus_tags')
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(4);
      if (error) throw new Error(error.message);
      return (data ?? []) as FeaturedProduct[];
    },
  });
}

/** 3 próximos eventos publicados que todavía no terminaron (incluye los en curso). */
export function useUpcomingEvents(): UseQueryResult<UpcomingEvent[], Error> {
  return useQuery<UpcomingEvent[], Error>({
    queryKey: ['events', 'upcoming'],
    queryFn: async (): Promise<UpcomingEvent[]> => {
      // ISO timestamp "now" calculado en cliente; la BD compara con `ends_at`
      // para que un evento en curso siga en "Próximos eventos".
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('events')
        .select(
          'id, title, slug, kind, modality, starts_at, ends_at, location_name, summary, hero_url, price_cop, event_categories(slug)',
        )
        .eq('status', 'upcoming')
        .eq('is_published', true)
        .is('deleted_at', null)
        .gte('ends_at', nowIso)
        .order('starts_at', { ascending: true })
        .limit(3);
      if (error) throw new Error(error.message);
      return (data ?? []) as UpcomingEvent[];
    },
  });
}

/** 3 testimonios publicados ordenados por `sort asc, created_at desc`. */
export function useTestimonials(): UseQueryResult<HomeTestimonial[], Error> {
  return useQuery<HomeTestimonial[], Error>({
    queryKey: ['testimonials'],
    queryFn: async (): Promise<HomeTestimonial[]> => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('id, author_name, avatar_url, body, rating')
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('sort', { ascending: true })
        .order('created_at', { ascending: false })
        .limit(3);
      if (error) throw new Error(error.message);
      return (data ?? []) as HomeTestimonial[];
    },
  });
}

/** Hasta 6 items de galería marcados como `featured_instagram` (bloque Instagram del inicio). */
export function useInstagramGallery(): UseQueryResult<InstagramGalleryItem[], Error> {
  return useQuery<InstagramGalleryItem[], Error>({
    queryKey: ['gallery', 'instagram'],
    queryFn: async (): Promise<InstagramGalleryItem[]> => {
      const { data, error } = await supabase
        .from('gallery_items')
        .select('id, title, slug, type, media_urls, cover_url')
        .eq('featured_instagram', true)
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('sort', { ascending: true })
        .limit(6);
      if (error) throw new Error(error.message);
      return (data ?? []) as InstagramGalleryItem[];
    },
  });
}
